import { useState, useMemo } from "react";
import { CheckCheck, XCircle, ChevronUp, ChevronDown, ChevronsUpDown, Clock } from "lucide-react";
import { countWorkingDays } from "@/utils/workingDays";
import { formatDate } from "@/utils/formatDate";

export interface RemoteWorkResolvedEntry {
  id: string;
  employee_name?: string;
  start_date: string;
  end_date: string;
  status: string;
  reviewed_by_name: string | null;
  review_comment: string;
  created_at: string;
}

type SortKey = "employee_name" | "start_date" | "status" | "reviewed_by_name" | "review_comment" | "created_at";
type SortDir = "asc" | "desc";

function SortIcon({ col, sortKey, sortDir }: { col: SortKey; sortKey: SortKey; sortDir: SortDir }) {
  if (col !== sortKey) return <ChevronsUpDown size={12} className="text-gray-300 dark:text-gray-600" />;
  return sortDir === "asc"
    ? <ChevronUp size={12} className="text-[var(--color-primary)]" />
    : <ChevronDown size={12} className="text-[var(--color-primary)]" />;
}

function StatusBadge({ status }: { status: string }) {
  if (status === "approved") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded-full whitespace-nowrap">
        <CheckCheck size={11} /> Zatwierdzono
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 px-2 py-0.5 rounded-full whitespace-nowrap">
      <XCircle size={11} /> Odrzucono
    </span>
  );
}

function sortEntries(entries: RemoteWorkResolvedEntry[], key: SortKey, dir: SortDir) {
  return [...entries].sort((a, b) => {
    const av = ((key === "employee_name" ? a.employee_name : a[key as keyof RemoteWorkResolvedEntry]) ?? "") as string;
    const bv = ((key === "employee_name" ? b.employee_name : b[key as keyof RemoteWorkResolvedEntry]) ?? "") as string;
    const cmp = av.localeCompare(bv, "pl", { sensitivity: "base" });
    return dir === "asc" ? cmp : -cmp;
  });
}

interface Props {
  entries: RemoteWorkResolvedEntry[];
  showEmployee?: boolean;
}

export function RemoteWorkResolvedTable({ entries, showEmployee = true }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>("start_date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  function handleSort(col: SortKey) {
    if (col === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col);
      setSortDir("asc");
    }
  }

  const sorted = useMemo(() => sortEntries(entries, sortKey, sortDir), [entries, sortKey, sortDir]);

  type ColDef = { col: SortKey; label: string; cls: string };
  const columns: ColDef[] = [
    ...(showEmployee ? [{ col: "employee_name" as SortKey, label: "Pracownik", cls: "" }] : []),
    { col: "start_date" as SortKey, label: "Termin", cls: "" },
    { col: "created_at" as SortKey, label: "Złożono", cls: "hidden sm:table-cell" },
    { col: "status" as SortKey, label: "Status", cls: "" },
    { col: "reviewed_by_name" as SortKey, label: "Rozpatrzył", cls: "hidden sm:table-cell" },
    { col: "review_comment" as SortKey, label: "Komentarz", cls: "hidden md:table-cell" },
  ];

  if (sorted.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 text-center">
        <Clock size={28} className="text-gray-300 dark:text-gray-600 mx-auto mb-2" />
        <p className="text-gray-400 dark:text-gray-500 text-sm">Brak rozpatrzonych wniosków o pracę zdalną.</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 overflow-hidden">
      <table className="w-full text-sm" data-testid="remote-resolved-table">
        <thead>
          <tr className="border-b border-gray-100 dark:border-gray-700 text-left">
            {columns.map(({ col, label, cls }) => (
              <th
                key={col}
                onClick={() => handleSort(col)}
                className={`px-4 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 ${cls}`}
              >
                <span className="inline-flex items-center gap-1">
                  {label}
                  <SortIcon col={col} sortKey={sortKey} sortDir={sortDir} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {sorted.map((entry) => (
            <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
              {showEmployee && (
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                  {entry.employee_name ?? "—"}
                </td>
              )}
              <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                {formatDate(entry.start_date)}
                {entry.start_date !== entry.end_date && <span> – {formatDate(entry.end_date)}</span>}
                {entry.start_date !== entry.end_date && (
                  <span className="ml-1 text-xs text-gray-400">
                    ({countWorkingDays(entry.start_date, entry.end_date)} d.r.)
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap hidden sm:table-cell text-xs">
                {entry.created_at
                  ? new Date(entry.created_at).toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
                  : <span className="text-gray-300 dark:text-gray-600">—</span>}
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={entry.status} />
              </td>
              <td className="px-4 py-3 text-gray-500 dark:text-gray-400 hidden sm:table-cell whitespace-nowrap">
                {entry.reviewed_by_name ?? <span className="text-gray-300 dark:text-gray-600">—</span>}
              </td>
              {/* Comment takes the leftover width — it is the only free-text column. */}
              <td className="px-4 py-3 text-gray-500 dark:text-gray-400 hidden md:table-cell w-full">
                {entry.review_comment ? (
                  <span className="italic text-xs">{entry.review_comment}</span>
                ) : (
                  <span className="text-gray-300 dark:text-gray-600">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
